"use client";

/**
 * FilePanelRenderer — rich panel-mode rendering for <FilePreview size="panel">.
 *
 * Restores the InlinePreviewPanel fidelity that the unified FilePreview's
 * snippet-based tile/card rendering dropped:
 *
 *   - Markdown      → MarkdownRenderer (full GFM, no 1200-char cap)
 *   - JSON          → pretty-printed in <pre>
 *   - CSV           → CsvTable (50-row cap)
 *   - .xlsx/.docx/.pptx → Microsoft Office Online iframe
 *   - HTML          → iframe src={url} sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox" (scripts run, links can open tabs)
 *   - PDF           → <iframe src={url}> (full viewer, no toolbar truncation)
 *   - image/video/audio → native elements with controls
 *   - code/text     → full-file <pre> (no snippet truncation)
 *   - binary other  → "Preview not available" message
 *
 * Preview / Source tabs are shown for text-fetchable categories
 * (text / code / spreadsheet). The Source tab always renders raw
 * <pre><code>{text}</code></pre> regardless of kind.
 *
 * The full-file fetch fires on mount (not gated on IntersectionObserver) —
 * the panel renders one file at a time so eager-fetching is fine.
 *
 * Uses the .fo-preview* CSS classes already scoped by .fpc-host so styling
 * stays consistent with the legacy InlinePreviewPanel.
 */

import { useEffect, useRef, useState } from "react";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import { cn } from "@/lib/utils";

// ── Local extension → panel-category map ────────────────────────────────
//
// file-category.ts collapses everything that isn't image/video/audio/html/pdf
// into 'text' or 'binary'. For rich panel rendering we need finer
// discrimination (code vs prose-text vs spreadsheet vs office-document)
// because the per-kind branch picks the renderer (MarkdownRenderer vs <pre>
// vs CsvTable vs Office Online iframe). Ported from the old EXT_CONFIG in
// FileOutputCarousel.tsx.

export type PanelCat =
  | "pdf"
  | "image"
  | "video"
  | "audio"
  | "text"
  | "code"
  | "spreadsheet"
  | "document"
  | "other";

const EXT_TO_CAT: Record<string, PanelCat> = {
  pdf: "pdf",
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  svg: "image",
  webp: "image",
  avif: "image",
  mp4: "video",
  webm: "video",
  mov: "video",
  m4v: "video",
  mp3: "audio",
  wav: "audio",
  ogg: "audio",
  m4a: "audio",
  flac: "audio",
  md: "text",
  mdx: "text",
  markdown: "text",
  txt: "text",
  log: "text",
  rtf: "text",
  json: "text",
  yaml: "text",
  yml: "text",
  xml: "text",
  toml: "text",
  html: "text",
  htm: "text",
  py: "code",
  js: "code",
  ts: "code",
  jsx: "code",
  tsx: "code",
  rb: "code",
  go: "code",
  rs: "code",
  java: "code",
  c: "code",
  cpp: "code",
  h: "code",
  css: "code",
  scss: "code",
  sh: "code",
  bash: "code",
  zsh: "code",
  sql: "code",
  swift: "code",
  kt: "code",
  php: "code",
  lua: "code",
  r: "code",
  csv: "spreadsheet",
  tsv: "spreadsheet",
  xlsx: "spreadsheet",
  docx: "document",
  pptx: "document",
};

function extOf(filename: string): string {
  const i = filename.lastIndexOf(".");
  return i > 0 ? filename.slice(i + 1).toLowerCase() : "";
}

function categorisePanel(filename: string): PanelCat {
  return EXT_TO_CAT[extOf(filename)] ?? "other";
}

/** Categories whose content is fetched as text so they can offer a Source tab. */
function isTextFetchable(cat: PanelCat): boolean {
  return cat === "text" || cat === "code" || cat === "spreadsheet";
}

// ── Hook: eager full-file fetch ─────────────────────────────────────────
//
// Unlike usePreviewText (which gates on IntersectionObserver + caps at 4KB
// for snippets), panel mode renders one file at a time and needs the full
// content for Markdown / CSV / JSON / Source-tab rendering. Plain fetch on
// mount mirrors the legacy InlinePreviewPanel useEffect.

function usePanelText(url: string | undefined, enabled: boolean) {
  // We track the URL the latest fetch was issued for + whatever body it
  // resolved with. `loading` is derived: if the latest issued URL doesn't
  // match the latest resolved URL (or there's nothing yet), we're loading.
  // This keeps every state update inside the async callbacks — no
  // synchronous setState in the effect body, which trips
  // react-hooks/set-state-in-effect.
  const [resolved, setResolved] = useState<{
    url: string;
    body: string;
  } | null>(null);
  const issuedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !url) return;
    if (issuedRef.current === url) return;
    issuedRef.current = url;
    let cancelled = false;
    fetch(url)
      .then((res) => res.text())
      .then((body) => {
        if (!cancelled) setResolved({ url, body });
      })
      .catch(() => {
        if (!cancelled)
          setResolved({ url, body: "Failed to load file content." });
      });
    return () => {
      cancelled = true;
    };
  }, [url, enabled]);

  const text = resolved && resolved.url === url ? resolved.body : null;
  const loading = enabled && !!url && text === null;
  return { text, loading };
}

// ── Component ───────────────────────────────────────────────────────────

interface Props {
  filename: string;
  url?: string;
}

type PreviewTab = "preview" | "source";

export function FilePanelRenderer({ filename, url }: Props) {
  const cat = categorisePanel(filename);
  const fetchable = isTextFetchable(cat);
  const [tab, setTab] = useState<PreviewTab>("preview");
  const { text, loading } = usePanelText(url, fetchable);

  if (!url) {
    return (
      <div
        className="fo-preview border-t border-border-subtle bg-bg-muted animate-fo-preview-in"
        data-preview-kind="panel-missing"
      >
        <div className="fo-preview__content max-h-125 overflow-auto">
          <div className="fo-preview__unsupported py-6 px-4 text-3 text-fg-subtle text-center">
            No file URL.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fo-preview border-t border-border-subtle bg-bg-muted animate-fo-preview-in"
      data-preview-kind="panel"
    >
      {fetchable && (
        <div className="fo-preview__tabs flex gap-0 border-b border-border-subtle py-0 px-4">
          <button
            className={cn(
              "fo-preview__tab font-[inherit] text-[11px] font-medium text-fg-subtle py-2 px-3 border-none bg-none cursor-pointer relative transition-colors hover:text-fg-default",
              tab === "preview"
                ? "fo-preview__tab--active text-fg-default after:[content:''] after:absolute after:-bottom-px after:left-3 after:right-3 after:h-0.5 after:bg-fg-default after:rounded-2"
                : ""
            )}
            onClick={() => setTab("preview")}
          >
            Preview
          </button>
          <button
            className={cn(
              "fo-preview__tab font-[inherit] text-[11px] font-medium text-fg-subtle py-2 px-3 border-none bg-none cursor-pointer relative transition-colors hover:text-fg-default",
              tab === "source"
                ? "fo-preview__tab--active text-fg-default after:[content:''] after:absolute after:-bottom-px after:left-3 after:right-3 after:h-0.5 after:bg-fg-default after:rounded-2"
                : ""
            )}
            onClick={() => setTab("source")}
          >
            Source
          </button>
        </div>
      )}

      <div className="fo-preview__content max-h-125 overflow-auto">
        {tab === "source" && fetchable ? (
          <SourceView text={text} loading={loading} />
        ) : (
          <PreviewView
            cat={cat}
            filename={filename}
            url={url}
            text={text}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
}

// ── Preview view: per-category dispatch ─────────────────────────────────

function PreviewView({
  cat,
  filename,
  url,
  text,
  loading,
}: {
  cat: PanelCat;
  filename: string;
  url: string;
  text: string | null;
  loading: boolean;
}) {
  switch (cat) {
    case "pdf":
      return (
        <iframe
          src={url}
          className="fo-preview__iframe w-full h-125 border-none block"
          title={filename}
          data-panel-renderer="pdf"
        />
      );
    case "image":
      return (
        // Using a plain <img> intentionally — these files are user-generated
        // signed URLs (Supabase Storage) and shouldn't go through next/image
        // optimisation. Same pattern as the legacy InlinePreviewPanel.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={url}
          alt={filename}
          className="fo-preview__image max-w-full max-h-125 block my-4 mx-auto rounded-6 [object-fit:contain]"
          data-panel-renderer="image"
        />
      );
    case "video":
      return (
        <video
          src={url}
          controls
          className="fo-preview__video w-full max-h-100 block rounded-[0]"
          data-panel-renderer="video"
        />
      );
    case "audio":
      return (
        <div className="fo-preview__audio-wrap p-4">
          <audio
            src={url}
            controls
            className="fo-preview__audio w-full"
            data-panel-renderer="audio"
          />
        </div>
      );
    case "code":
      if (loading)
        return (
          <div className="fo-preview__loading py-6 px-4 text-3 text-fg-subtle text-center">
            Loading...
          </div>
        );
      return (
        <pre
          className="fo-preview__code m-0 py-3.5 px-4 font-mono text-3 leading-[1.6] text-fg-default [white-space:pre-wrap] [word-break:break-word] overflow-x-auto [tab-size:2]"
          data-panel-renderer="code"
        >
          <code>{text ?? ""}</code>
        </pre>
      );
    case "text": {
      if (loading)
        return (
          <div className="fo-preview__loading py-6 px-4 text-3 text-fg-subtle text-center">
            Loading...
          </div>
        );
      const ext = extOf(filename);
      // HTML — render live in a sandboxed iframe straight from the URL so
      // scripts run (panel mode is on a real URL, no 32KB srcDoc cap).
      if (ext === "html" || ext === "htm") {
        return (
          <iframe
            src={url}
            className="fo-preview__iframe w-full h-125 border-none block"
            title={filename}
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
            data-panel-renderer="html"
          />
        );
      }
      // Markdown — full GFM via MarkdownRenderer.
      if ((ext === "md" || ext === "mdx" || ext === "markdown") && text) {
        return (
          <div
            className="fo-preview__markdown py-4 px-5 text-3.5 leading-[1.6] overflow-y-auto max-h-125"
            data-panel-renderer="markdown"
          >
            <MarkdownRenderer content={text} />
          </div>
        );
      }
      // JSON — pretty-print if it parses, else fall through to raw text.
      if (ext === "json" && text) {
        const pretty = tryPrettyJson(text);
        if (pretty !== null) {
          return (
            <pre
              className="fo-preview__code m-0 py-3.5 px-4 font-mono text-3 leading-[1.6] text-fg-default [white-space:pre-wrap] [word-break:break-word] overflow-x-auto [tab-size:2]"
              data-panel-renderer="json"
            >
              <code>{pretty}</code>
            </pre>
          );
        }
      }
      return (
        <pre
          className="fo-preview__code fo-preview__code--text m-0 py-3.5 px-4 font-mono text-3 leading-[1.6] text-fg-default [white-space:pre-wrap] [word-break:break-word] overflow-x-auto [tab-size:2] [font-family:inherit] text-3.5 leading-[1.55]"
          data-panel-renderer="text"
        >
          <code>{text ?? ""}</code>
        </pre>
      );
    }
    case "spreadsheet": {
      const ext = extOf(filename);
      // xlsx — Microsoft Office Online viewer. Renders immediately from
      // the URL; no need to wait for the text fetch that gets fired
      // (spreadsheet is text-fetchable so the Source tab works).
      if (ext === "xlsx") {
        return (
          <iframe
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
              url
            )}`}
            className="fo-preview__iframe w-full h-125 border-none block"
            title={filename}
            data-panel-renderer="office"
          />
        );
      }
      if (loading)
        return (
          <div className="fo-preview__loading py-6 px-4 text-3 text-fg-subtle text-center">
            Loading...
          </div>
        );
      if (!text)
        return (
          <div className="fo-preview__loading py-6 px-4 text-3 text-fg-subtle text-center">
            No content
          </div>
        );
      return <CsvTable csv={text} />;
    }
    case "document":
      // docx / pptx → Office Online viewer.
      return (
        <iframe
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
            url
          )}`}
          className="fo-preview__iframe w-full h-125 border-none block"
          title={filename}
          data-panel-renderer="office"
        />
      );
    default:
      return (
        <div className="fo-preview__unsupported py-6 px-4 text-3 text-fg-subtle text-center">
          Preview not available. Use Open or Download.
        </div>
      );
  }
}

/**
 * Try to JSON.parse + pretty-print a text body. Returns null if the
 * body isn't valid JSON, so the caller can fall through to raw-text
 * rendering. Keeps the try/catch out of the JSX tree.
 */
function tryPrettyJson(text: string): string | null {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return null;
  }
}

function SourceView({
  text,
  loading,
}: {
  text: string | null;
  loading: boolean;
}) {
  if (loading)
    return (
      <div className="fo-preview__loading py-6 px-4 text-3 text-fg-subtle text-center">
        Loading...
      </div>
    );
  return (
    <pre
      className="fo-preview__code m-0 py-3.5 px-4 font-mono text-3 leading-[1.6] text-fg-default [white-space:pre-wrap] [word-break:break-word] overflow-x-auto [tab-size:2]"
      data-panel-renderer="source"
    >
      <code>{text ?? ""}</code>
    </pre>
  );
}

// ── CsvTable ────────────────────────────────────────────────────────────
//
// Parses CSV (handling quoted fields with commas) and renders as a table.
// Caps at 50 rows; appends a "Showing 50 of N" hint if truncated.

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (ch === "," && !inQuotes) {
      out.push(cur.trim());
      cur = "";
      continue;
    }
    cur += ch;
  }
  out.push(cur.trim());
  return out;
}

function CsvTable({ csv }: { csv: string }) {
  const lines = csv.trim().split("\n");
  if (lines.length === 0) return null;
  const headers = parseCsvLine(lines[0]);
  const rows = lines.slice(1, 51).map(parseCsvLine);
  return (
    <div
      className="fo-preview__table-wrap overflow-x-auto p-0"
      data-panel-renderer="csv"
    >
      <table className="fo-preview__table w-full [border-collapse:collapse] text-3 [font-variant-numeric:tabular-nums] [&_th]:text-left [&_th]:font-semibold [&_th]:text-fg-default [&_th]:py-2 [&_th]:px-3 [&_th]:[border-bottom:2px_solid_var(--border-default)] [&_th]:bg-bg-elevated [&_th]:sticky [&_th]:top-0 [&_th]:whitespace-nowrap [&_td]:py-1.5 [&_td]:px-3 [&_td]:border-b [&_td]:border-border-subtle [&_td]:text-fg-muted [&_td]:whitespace-nowrap [&_tr:hover_td]:bg-file-panel-renderer-bg-elevated-tint">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {lines.length > 51 && (
        <div className="fo-preview__table-more py-2 px-3 text-3 text-fg-faint text-center border-t border-border-subtle">
          Showing 50 of {lines.length - 1} rows
        </div>
      )}
    </div>
  );
}
